import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';

import { basicReducer } from '../../../../utils/reducers';

import useStyles from './styles';

/**A React Hook to manage the state and invoke APIs required for the FeesTable component.
 *
 * @returns {Object} Handler functions and State variables necessary for the function of the FeesTable component. i.e. isDoingAjax, initialize, addGroup
 */
const useFeesTable = () => {
    const [state, dispatch] = React.useReducer(basicReducer, {
        isInitialized: false,
        isDoingAjax: false,
        sets: [],
        showDeleteConfirmation: false,
    });

    const styles = useStyles();

    let stripCommas = (_fee) => _fee.Amount?.replace?.(/,/g, '') || _fee.Amount;

    let validateDescription = (_fee) => {        
        let error = false,
            errorMessage = null;
        if (_fee.FeeDescription === '') {
            error = true;
            errorMessage = 'Fee description is required.';
        } else if(_fee.FeeDescription.length > 50) {
            error = true;
            errorMessage = 'Fee description must be less than or equal to 50 characters.';
        }

        return {
            error, errorMessage
        }
    };

    let validateAmount = (_fee) => {
        let error = false,
            errorMessage = null;
        let currencyRegex = new RegExp(/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{1,2})?$/g);
        let parsedFeeAmount = parseFloat(stripCommas(_fee));
        if (!currencyRegex.test(_fee.Amount)) {
            error = true;
            errorMessage = 'Only numerical, commas and dots are allowed.';
        } else if(parsedFeeAmount === 0 || isNaN(parsedFeeAmount)) {
            error = true;
            errorMessage = 'Amount is required.';
        } 

        return {
            error, errorMessage
        };
    };

    const initialize = async (schoolId) => {
        dispatch({
            type: 'doingAjax',
        });

        try {
            let _gradeLevels = await API.post('ShipyardAPI', '/schoolsettings/grade_level_group/retrieve', {
                body: {
                    schoolId,
                },
            });

            _gradeLevels = _gradeLevels.map((curr) => {
                var selectedLevels = Object.entries(curr.levels);
                selectedLevels = selectedLevels.filter(([key, value]) => value === true);
                selectedLevels = selectedLevels.map(([key, value]) => key);

                var validatedFees = curr?.fees?.map(fee => {
                    let {
                        error: descErr, errorMessage: descErrMsg
                    } = validateDescription(fee);
        
                    let {
                        error: amtErr, errorMessage: amtErrMsg
                    } = validateAmount(fee);

                    return {
                        ...fee,
                        nameError: descErr,
                        nameErrorMessage: descErrMsg,
                        showNameError: false,
                        amountError: amtErr,
                        amountErrorMessage: amtErrMsg,
                        showAmountError: false,
                    }
                });


                return {
                    ...curr,
                    fees: validatedFees,
                    initialFees: curr.fees,
                    selectedLevels,
                    levelError: selectedLevels?.length === 0,
                    levelErrorMessage: selectedLevels?.length === 0 ? 'At least one grade level must be selected.' : null,
                    showLevelError: false,
                };
            });

            dispatch({
                type: 'update',
                newState: {
                    isInitialized: true,
                    sets: _gradeLevels,
                    chosenGrades: _gradeLevels.flatMap((ent) => ent.selectedLevels),
                    showDeleteConfirmation: false,
                },
            });
        } catch (e) {
            toast.error('API error retrieving grade level groups.');
            console.error(e);
        } finally {
            dispatch({
                type: 'ajaxDone',
            });
        }
    };

    const addGroup = async (schoolId) => {
        dispatch({
            type: 'doingAjax',
        });

        let groupNumber = state?.sets?.[state?.sets?.length - 1]?.GroupNumber + 1 || 1;

        try {
            await API.post('ShipyardAPI', '/schoolsettings/grade_level_group', {
                body: {
                    schoolId,
                    groupNumber,
                },
            });

            toast.success(`Successfully created Grade Level Group ${groupNumber}.`)
        } catch (e) {
            toast.error('API error.');
        } finally {
            await initialize(schoolId);
        }
    };

    const deleteGroup = async (schoolId, groupNumber) => {
        dispatch({
            type: 'doingAjax',
        });

        try {
            await API.del('ShipyardAPI', '/schoolsettings/grade_level_group', {
                body: {
                    schoolId,
                    groupNumber,
                },
            });

            toast.success(`Successfully deleted Grade Level Group ${groupNumber}`);
        } catch (e) {
            toast.error('API error.');
        } finally {
            await initialize(schoolId);
        }
    };

    const cancelDelete = () => dispatch({
        type: 'update',
        newState: {
            showDeleteConfirmation: false,
            confirmationMessageTitle: null,
            confirmationMessage: null,
            onDeleteConfirm: () => null,
            onDeleteCancel: () => null,
        }
    });

    const confirmDeleteGroup = (schoolId, groupNumber) => {
        dispatch({
            type: 'update',
            newState: {
                showDeleteConfirmation: true,
                confirmationMessageTitle: `Delete Group ${groupNumber}?`,
                confirmationMessage: `Are you sure you want to delete Grade Level Group ${groupNumber}?`,
                onDeleteConfirm: () => deleteGroup(schoolId, groupNumber),
                onDeleteCancel: () => cancelDelete(),
            }
        });
    };

    const addFee = async (level, schoolId) => {
        dispatch({
            type: 'doingAjax',
        });

        try {
            let relevantLevel = state.sets.find((_level) => _level.GradeLevelGroupID === level);
            await API.post('ShipyardAPI', '/schoolsettings/grade_level_group/fee', {
                body: {
                    GradeLevelID: relevantLevel.GradeLevelGroupID,
                    Ordinal: relevantLevel?.fees?.length + 1 || 2,
                },
            });

            toast.success(`Fee added successfully to Grade Level Group ${relevantLevel?.GroupNumber}.`);
        } catch (e) {
            toast.error('API error.');
            console.error('APIError', e);
        } finally {
            await initialize(schoolId);
        }
    };

    const updateFee = async (level, feeId, key, newVal) => {
        dispatch({
            type: 'update',
            newState: {
                sets: state.sets.map((currLevel) => {
                    if (currLevel.GradeLevelGroupID === level) {
                        return {
                            ...currLevel,
                            fees: currLevel.fees.map((currFee) => {
                                if (currFee.GroupFeeID === feeId) {
                                    return {
                                        ...currFee,
                                        [key === 'FeeDescription' ? 'showNameError' : 'showAmountError']: false,
                                        [key]: newVal,
                                    };
                                } else {
                                    return currFee;
                                }
                            }),
                        };
                    } else {
                        return currLevel;
                    }
                }),
            },
        });
    };

    const validateFeeDescription = (level, feeId) => {
        let _fee = state.sets.find((_level) => _level.GradeLevelGroupID === level).fees.find((fee) => fee.GroupFeeID === feeId);

        let { error, errorMessage } = validateDescription(_fee);
        
        dispatch({
            type: 'update',
            newState: {
                sets: state.sets.map((currLevel) => {
                    if (currLevel.GradeLevelGroupID === level) {
                        return {
                            ...currLevel,
                            fees: currLevel.fees.map((currFee) => {
                                if (currFee.GroupFeeID === feeId) {
                                    return {
                                        ...currFee,
                                        showNameError: true,
                                        nameError: error,
                                        nameErrorMessage: errorMessage,
                                    };
                                } else {
                                    return currFee;
                                }
                            }),
                        };
                    } else {
                        return currLevel;
                    }
                }),
            },
        });
    };

    const validateFeeAmount = (level, feeId) => {
        let _fee = state.sets.find((_level) => _level.GradeLevelGroupID === level).fees.find((fee) => fee.GroupFeeID === feeId);

        let { error, errorMessage } = validateAmount(_fee);
        let amountNoCommas = _fee.Amount?.replace?.(/,/g, '') || _fee.Amount;

        dispatch({
            type: 'update',
            newState: {
                sets: state.sets.map((currLevel) => {
                    if (currLevel.GradeLevelGroupID === level) {
                        return {
                            ...currLevel,
                            fees: currLevel.fees.map((currFee) => {
                                if (currFee.GroupFeeID === feeId) {
                                    return {
                                        ...currFee,
                                        Amount: error ? currFee.Amount : parseFloat(amountNoCommas),
                                        showAmountError: true,
                                        amountError: error,
                                        amountErrorMessage: errorMessage,
                                    };
                                } else {
                                    return currFee;
                                }
                            }),
                        };
                    } else {
                        return currLevel;
                    }
                }),
            },
        });
    };
    
    const saveRow = async (event, levelId, feeId) => {
        if(!event.currentTarget.contains(event.relatedTarget)) {
            let _relevantLevel = state.sets.find((_level) => _level.GradeLevelGroupID === levelId);
            let _fee = _relevantLevel.fees.find((fee) => fee.GroupFeeID === feeId);
            let _initialFee = state.sets.find((_level) => _level.GradeLevelGroupID === levelId).initialFees.find((fee) => fee.GroupFeeID === feeId);

            let feeUnchanged = _fee?.FeeDescription === _initialFee?.FeeDescription && _fee?.Amount === _initialFee?.Amount;
            let feeInvalid = validateDescription(_fee)?.error === true || validateAmount(_fee)?.error === true;

            if(feeUnchanged || feeInvalid) return;

            let amountNoCommas = stripCommas(_fee);

            dispatch({
                type: 'doingAjax'
            });

            try {
                await API.patch('ShipyardAPI', '/schoolsettings/grade_level_group/fee', {
                    body: {
                        GradeLevelID: levelId,
                        FeeDescription: _fee.FeeDescription,
                        Amount: parseFloat(amountNoCommas),
                        IsTuition: _fee.IsTuition,
                        Ordinal: _fee.Ordinal,
                    },
                });
                let successTarget = _fee.Ordinal === 1 ? 'Annual Tuition' : `Fee ${_fee.Ordinal - 1}`;

                toast.success(`Successfully updated ${successTarget} of Group ${_relevantLevel.GroupNumber}.`);
            } catch (e) {
                toast.error('API error.');
            } finally {
                dispatch({
                    type: 'ajaxDone',
                });
            }
        }
        else return;
    };

    const deleteFee = async (feeId, schoolId) => {
        dispatch({
            type: 'doingAjax',
        });

        try {
            await API.del('ShipyardAPI', '/schoolsettings/grade_level_group/fee', {
                body: {
                    feeId: `${feeId}`,
                },
            });

            toast.success('Fee deleted successfully.');
        } catch (e) {
            toast.error("Couldn't find fee.");
        } finally {
            await initialize(schoolId);
        }
    };

    const handleGradeChange = async (item, gradeLevelGroupId) => {
        let _set = state.sets.find((set) => set.GradeLevelGroupID === gradeLevelGroupId);

        let _chosenGrades = new Set(state.chosenGrades);
        let _chosenSetLevels = item;
        let _previousSetLevels = _set.selectedLevels;
        let _deselectedLevels = _previousSetLevels.filter(x => !_chosenSetLevels.includes(x));

        _deselectedLevels?.forEach?.((ent) => {
            _chosenGrades.delete(ent);
        });

        item.forEach((ent) => _chosenGrades.add(ent));

        dispatch({
            type: 'update',
            newState: {
                chosenGrades: Array.from(_chosenGrades),
                sets: state.sets.map((set) => {
                    if (set.GradeLevelGroupID === gradeLevelGroupId) {
                        return {
                            ..._set,
                            selectedLevels: item,
                            levelError: item?.length === 0,
                            levelErrorMessage: item?.length === 0 ? 'At least one grade level must be selected.' : null,
                        };
                    } else return set;
                }),
            },
        });
    };

    const handleGradeSave = async (schoolId, groupNumber) => {
        let set = state.sets.find((_set) => _set.GroupNumber === groupNumber);

        if (set?.selectedLevels?.length === 0) {
            dispatch({
                type: 'update',
                newState: {
                    sets: state?.sets?.map(_set => {
                        if(_set?.GroupNumber === groupNumber) {
                            return {
                                ...set,
                                levelError: true,
                                levelErrorMessage: 'At least one grade level must be selected.',
                                showLevelError: true,
                            }
                        } else return _set;
                    })
                }
            });
        }

        dispatch({
            type: 'doingAjax',
        });

        try {
            await API.patch('ShipyardAPI', '/schoolsettings/grade_level_group', {
                body: {
                    schoolId,
                    groupNumber,
                    gradeSet: set.selectedLevels,
                },
            });

            toast.success(`Grade levels updated for Grade Level Group ${groupNumber}.`)
        } catch (e) {
            toast.error('API error.');
        } finally {
            await initialize(schoolId);
        }
    };

    const showErrors = () => dispatch({
        type: 'update',
        newState: {
            sets: state?.sets?.map(set => {
                return {
                    ...set,
                    showLevelError: true,
                    fees: set?.fees?.map?.(fee => {
                        return {
                            ...fee,
                            showAmountError: true,
                            showNameError: true,
                        }
                    })
                }
            })
        }
    });

    return {
        ...state,
        ...styles,
        initialize,
        addGroup,
        confirmDeleteGroup,
        deleteGroup,
        addFee,
        updateFee,
        validateFeeDescription,
        validateFeeAmount,
        saveRow,
        deleteFee,
        handleGradeChange,
        handleGradeSave,
        showErrors,
    };
};

export default useFeesTable;

